import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { printTemplates as api } from '../../api/endpoints';
import { getList } from '../../api/client';
import { useChurchConfig } from '../../hooks';
import {
  PRINT_DOCUMENTS, DOCUMENT_CATEGORIES, documentFor, documentLabel,
  fieldsFor, sampleFor, missingFields
} from '../../utils/printDocuments';
import type { PrintDocumentDef } from '../../utils/printDocuments';
import { renderLayout } from '../../utils/printLayouts';
import {
  PRINT_TEMPLATE_LIST, DEFAULT_PRINT_TEMPLATE, resolvePrintTemplate
} from '../../theme/PrintTemplate';
import { brand } from '../../theme/tokens';
import LayoutThumbnail, { layoutAttributes } from './LayoutThumbnail';
import { buildPrintHtml } from '../../utils/printService';
import type { PrintTemplate } from '../../types';
import {
  Card, Button, Badge, Input, Select, Modal, ConfirmDialog, FormLayout, SearchBar
} from '../../components/ui';
import type { SettingsSectionProps } from './SettingsPage';

// How each document looks on paper, arranged the way an administrator thinks
// about it: a list of the documents this church prints, each showing the layout
// it currently uses.
//
// The module and document-type codes a template is filed under stay internal.
// HTML is never the first thing on screen — an administrator picks a layout and
// sees it; only "Edit" opens the markup.

const isLive = (t: PrintTemplate) => t.status !== 'VOID';

/** Enough of a check to stop a template that cannot render. */
const validate = (t: PrintTemplate): string[] => {
  const problems: string[] = [];
  if (!t.templateName?.trim()) problems.push('Give the template a name.');
  if (!t.documentType) problems.push('Choose which document this is for.');
  if (!t.htmlContent?.trim()) problems.push('The template is empty.');

  const html = `${t.headerHtml || ''}${t.htmlContent || ''}${t.footerHtml || ''}`;
  const opened = Array.from(html.matchAll(/\{\{[#^]\s*([\w.]+)\s*\}\}/g)).map((m) => m[1]);
  const closed = Array.from(html.matchAll(/\{\{\/\s*([\w.]+)\s*\}\}/g)).map((m) => m[1]);
  opened.forEach((n) => {
    if (!closed.includes(n)) problems.push(`A repeating section for "${n}" was opened but never closed.`);
  });
  closed.forEach((n) => {
    if (!opened.includes(n)) problems.push(`A repeating section for "${n}" was closed but never opened.`);
  });
  return problems;
};

const PAGE_SIZES = [
  { value: 'A4', label: 'A4' }, { value: 'A5', label: 'A5' },
  { value: 'LETTER', label: 'Letter' }, { value: 'LEGAL', label: 'Legal' }
];
const ORIENTATIONS = [
  { value: 'PORTRAIT', label: 'Portrait' }, { value: 'LANDSCAPE', label: 'Landscape' }
];

type View =
  | { screen: 'list' }
  | { screen: 'document'; doc: PrintDocumentDef }
  | { screen: 'choose'; doc: PrintDocumentDef }
  | { screen: 'edit'; template: PrintTemplate };

const TemplatesSection = ({ notify, t, isDarkMode }: SettingsSectionProps) => {
  const config = useChurchConfig();

  // A design is a shape any parish can wear: the ink comes from the brand
  // colour on the Church Identity tab, so the cards below show the real colour
  // rather than a sample one.
  const accent = config?.themeColor || brand.primary;

  const [templates, setTemplates] = useState<PrintTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<View>({ screen: 'list' });
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [previewing, setPreviewing] = useState<PrintTemplate | null>(null);
  const [confirming, setConfirming] = useState<{ template: PrintTemplate; doc: PrintDocumentDef } | null>(null);
  const [deleting, setDeleting] = useState<PrintTemplate | null>(null);
  const [creating, setCreating] = useState<{ name: string; documentType: string; basedOn: string } | null>(null);
  // Which design the picker is showing. It starts on whatever is in use, so
  // opening the screen shows the parish its own paper first.
  const [previewDesign, setPreviewDesign] = useState<string>('');
  // The "one design everywhere" dialog, and how far through it is.
  const [bulk, setBulk] = useState<{ design: string; running: boolean; done: number } | null>(null);

  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  /** A saved layout older than a field its document now needs, said plainly. */
  const missingNote = (x: PrintTemplate | undefined) => {
    if (!x) return null;
    const missing = missingFields(x.documentType, `${x.headerHtml || ''}${x.htmlContent || ''}${x.footerHtml || ''}`);
    return missing.length ? (
      <div style={{ fontSize: t.fontSize.sm, color: t.warning, marginTop: '3px' }}>
        ⚠ This layout does not print: {missing.join(', ')}. Choose the layout again or edit it to add {missing.length > 1 ? 'them' : 'it'}.
      </div>
    ) : null;
  };

  // Every module keeps its own templates, so the module is named wherever a
  // document is — several modules print a document called "Register".
  const moduleChip = (module?: string) => (module ? (
    <span style={{
      fontSize: t.fontSize.xs, fontWeight: 'bold', color: t.textAccent,
      backgroundColor: t.primarySoft, border: `1px solid ${t.border}`,
      borderRadius: t.radius.pill, padding: '2px 9px', whiteSpace: 'nowrap'
    }}>
      {module}
    </span>
  ) : null);
  const documentOptions = useMemo(
    () => [...PRINT_DOCUMENTS]
      .sort((a, b) => a.module.localeCompare(b.module) || a.label.localeCompare(b.label))
      .map((d) => ({ value: d.key, label: `${d.module} › ${d.label} (${d.category})` })),
    []
  );

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      setTemplates(getList<PrintTemplate>(await api.list()).filter(isLive));
    } catch {
      notify('Could not load the print templates.', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { refetch(); }, [refetch]);

  /** Templates filed under one document, and which of them is in use. */
  // The server resolves on the module *and* the document, so this screen has to
  // match on both. Filing a row under the wrong module would otherwise show a
  // template here that the printer never reaches for.
  const forDocument = useCallback(
    (key: string) => {
      const module = documentFor(key)?.module;
      return templates.filter((x) => x.documentType === key && (!module || x.module === module));
    },
    [templates]
  );
  const currentFor = useCallback(
    (key: string) => {
      const mine = forDocument(key).filter((x) => x.isActive);
      // The same order PrintTemplateService.resolve() uses: the default, and
      // failing that the oldest row — not whichever the list happened to return
      // first, or "In use" here would name a different layout from the one that
      // prints.
      return mine.find((x) => x.isDefault)
        || [...mine].sort((a, b) => Number(a.id) - Number(b.id))[0];
    },
    [forDocument]
  );

  const visibleDocuments = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return PRINT_DOCUMENTS.filter((d) => {
      if (category !== 'All' && d.category !== category) return false;
      if (!needle) return true;
      return `${d.label} ${d.describes} ${d.module}`.toLowerCase().includes(needle);
    });
  }, [search, category]);

  // --- Actions --------------------------------------------------------------

  const applyTemplate = async (template: PrintTemplate, doc: PrintDocumentDef) => {
    setSaving(true);
    try {
      await api.setDefault(template.id as number);
      notify(`✓ ${doc.label} now prints with "${template.templateName}".`, 'success');
      setConfirming(null);
      await refetch();
      setView({ screen: 'document', doc });
    } catch (err) {
      notify((err as Error)?.message || 'Could not change the template.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const saveTemplate = async (template: PrintTemplate) => {
    const problems = validate(template);
    if (problems.length) { notify(problems[0], 'error'); return; }
    setSaving(true);
    try {
      if (template.id) await api.update(template.id, template);
      else await api.create(template);
      notify(template.id ? 'Template saved.' : 'Template created.', 'success');
      await refetch();
      const doc = documentFor(template.documentType);
      setView(doc ? { screen: 'document', doc } : { screen: 'list' });
    } catch (err) {
      notify((err as Error)?.message || 'Could not save the template.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (template: PrintTemplate) => {
    try {
      await api.update(template.id as number, {
        ...template, isActive: !template.isActive, isDefault: false
      });
      notify(template.isActive ? 'Template deactivated.' : 'Template activated.', 'success');
      await refetch();
    } catch (err) {
      notify((err as Error)?.message || 'Could not change the template.', 'error');
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await api.softDelete(deleting.id as number, 'Removed from Settings');
      notify('Template deleted.', 'success');
      setDeleting(null);
      await refetch();
    } catch (err) {
      notify((err as Error)?.message || 'Could not delete the template.', 'error');
    }
  };

  /** A new template starts from a design rather than an empty page. */
  const firstLayoutValue = (documentType: string) =>
    (documentType ? `layout:${DEFAULT_PRINT_TEMPLATE}` : 'blank');

  const startCreate = (documentType?: string) =>
    setCreating({
      name: '',
      documentType: documentType || '',
      basedOn: firstLayoutValue(documentType || '')
    });

  const doCreate = () => {
    if (!creating) return;
    if (!creating.name.trim()) { notify('Give the template a name.', 'error'); return; }
    if (!creating.documentType) { notify('Choose which document this template is for.', 'error'); return; }

    const doc = documentFor(creating.documentType);
    // "Start from" offers the ready-made layouts by name and the parish's own
    // layouts by id; a copy carries the source's page setup, a ready-made one
    // starts at the standard A4 setup below.
    const layout = creating.basedOn.startsWith('layout:')
      ? renderLayout(creating.basedOn.slice(7), creating.documentType, accent)
      : undefined;
    const source = !layout && creating.basedOn !== 'blank'
      ? templates.find((x) => String(x.id) === creating.basedOn)
      : undefined;

    setCreating(null);
    setView({
      screen: 'edit',
      template: {
        templateName: creating.name.trim(),
        module: doc?.module || '',
        documentType: creating.documentType,
        htmlContent: layout?.htmlContent || source?.htmlContent || '',
        headerHtml: source?.headerHtml || '',
        footerHtml: source?.footerHtml || '',
        pageSize: source?.pageSize || 'A4',
        orientation: source?.orientation || 'PORTRAIT',
        marginTop: source?.marginTop ?? 15,
        marginRight: source?.marginRight ?? 15,
        marginBottom: source?.marginBottom ?? 15,
        marginLeft: source?.marginLeft ?? 15,
        isActive: true,
        isDefault: forDocument(creating.documentType).length === 0
      }
    });
  };

  // --- Shared pieces --------------------------------------------------------

  const statusBadges = (x: PrintTemplate) => (
    <>
      {x.isDefault && <Badge tone="success">In use</Badge>}
      {!x.isActive && <Badge tone="neutral">Inactive</Badge>}
    </>
  );

  const previewButton = (x: PrintTemplate, size: 'sm' | 'md' = 'sm') => (
    <Button variant="ghost" size={size} onClick={() => setPreviewing(x)}>Preview</Button>
  );

  /**
   * The design a saved layout was built from, so the picker can show which
   * card is in use. The name is the design's own label, which is what a
   * ready-made layout is saved under.
   */
  const designIdOf = (x?: PrintTemplate | null): string =>
    PRINT_TEMPLATE_LIST.find((d) => d.label === x?.templateName)?.id || '';

  /** Picking a design saves it for this document and makes it the one in use. */
  /** Files one design under one document and makes it the one in use. */
  const saveDesignFor = async (designId: string, doc: PrintDocumentDef) => {
    const built = renderLayout(designId, doc.key, accent);
    // Picking the same design again should return to it, not leave the parish
    // with three rows all called "Frame".
    const already = forDocument(doc.key).find((x) => x.templateName === built.templateName);
    if (already) {
      await api.update(already.id as number, { ...already, ...built, id: already.id, isDefault: true });
      await api.setDefault(already.id as number);
    } else {
      await api.create({ ...built, isDefault: true });
    }
    return built.templateName;
  };

  const applyDesign = async (designId: string, doc: PrintDocumentDef) => {
    setSaving(true);
    try {
      const name = await saveDesignFor(designId, doc);
      notify(`\u2713 ${doc.label} now prints with "${name}".`, 'success');
      await refetch();
      setView({ screen: 'document', doc });
    } catch (err) {
      notify((err as Error)?.message || 'Could not use that layout.', 'error');
    } finally {
      setSaving(false);
    }
  };

  /**
   * One design across every document, for a parish that wants its paper to
   * match rather than setting twenty-three documents by hand. Each document
   * still gets its own row afterwards, so any one of them can be changed later
   * without disturbing the rest.
   */
  const applyDesignToAll = async (designId: string) => {
    setBulk((prev) => (prev ? { ...prev, running: true, done: 0 } : prev));
    let done = 0;
    const failed: string[] = [];

    // Written one at a time on purpose: the server stands down the previous
    // default as each one lands, and twenty-three at once would race it.
    for (const d of PRINT_DOCUMENTS) {
      try {
        await saveDesignFor(designId, d);
      } catch {
        failed.push(d.label);
      }
      done += 1;
      const at = done;
      setBulk((prev) => (prev ? { ...prev, done: at } : prev));
    }

    await refetch();
    setBulk(null);
    const name = resolvePrintTemplate(designId).label;
    if (failed.length) {
      notify(`"${name}" applied to ${PRINT_DOCUMENTS.length - failed.length} documents. Failed: ${failed.join(', ')}.`, 'error');
    } else {
      notify(`\u2713 Every document now prints with "${name}".`, 'success');
    }
  };

  // --- Advanced editor ------------------------------------------------------

  if (view.screen === 'edit') {
    const template = view.template;
    const doc = documentFor(template.documentType);
    const groups = fieldsFor(template.documentType);
    const problems = validate(template);

    const set = <K extends keyof PrintTemplate>(key: K, value: PrintTemplate[K]) =>
      setView({ screen: 'edit', template: { ...template, [key]: value } });

    const insert = (token: string) => {
      const field = bodyRef.current;
      const text = `{{${token}}}`;
      if (!field) { set('htmlContent', `${template.htmlContent || ''}${text}`); return; }
      const start = field.selectionStart ?? field.value.length;
      const end = field.selectionEnd ?? start;
      set('htmlContent', field.value.slice(0, start) + text + field.value.slice(end));
      window.requestAnimationFrame(() => {
        field.focus();
        field.setSelectionRange(start + text.length, start + text.length);
      });
    };

    return (
      <FormLayout
        title={`Edit Layout — ${template.templateName || 'Template'}`}
        submitLabel={saving ? 'Saving…' : 'Save Layout'}
        submitting={saving}
        onSubmit={(e) => { e.preventDefault(); saveTemplate(template); }}
        onCancel={() => setView(doc ? { screen: 'document', doc } : { screen: 'list' })}
        cancelLabel="Back"
        maxWidth="100%"
      >
        <Card style={{ marginBottom: '18px', padding: '12px 14px', color: t.textMuted, fontSize: t.fontSize.md }}>
          This is the advanced editor. It writes the HTML for <strong style={{ color: t.textMain }}>{doc?.label || 'this document'}</strong>
          {doc?.module && <> in <strong style={{ color: t.textMain }}>{doc.module}</strong></>}.
          Click a field on the right to drop it in — the values are filled when the document prints.
        </Card>

        <FormLayout.Grid columns={2}>
          <Input
            label="Template Name" required
            value={template.templateName || ''}
            onChange={(e) => set('templateName', e.target.value)}
          />
          <Select
            label="For which document" required
            placeholder="Choose a document…"
            options={documentOptions}
            value={template.documentType || ''}
            onChange={(e) => {
              const key = e.target.value;
              const next = documentFor(key);
              setView({
                screen: 'edit',
                template: {
                  ...template,
                  documentType: key,
                  module: next?.module || '',
                  htmlContent: template.htmlContent?.trim()
                    ? template.htmlContent
                    : renderLayout(DEFAULT_PRINT_TEMPLATE, key, accent).htmlContent || ''
                }
              });
            }}
            isDarkMode={isDarkMode}
          />
        </FormLayout.Grid>

        <FormLayout.Section title="Page" />
        <FormLayout.Grid columns={6}>
          <Select
            label="Paper" options={PAGE_SIZES} value={template.pageSize || 'A4'}
            onChange={(e) => set('pageSize', e.target.value as PrintTemplate['pageSize'])}
            isDarkMode={isDarkMode}
          />
          <Select
            label="Orientation" options={ORIENTATIONS} value={template.orientation || 'PORTRAIT'}
            onChange={(e) => set('orientation', e.target.value as PrintTemplate['orientation'])}
            isDarkMode={isDarkMode}
          />
          {(['marginTop', 'marginRight', 'marginBottom', 'marginLeft'] as const).map((key) => (
            <Input
              key={key}
              label={`${key.replace('margin', '')} margin (mm)`}
              type="number" min="0"
              value={String(template[key] ?? '')}
              onChange={(e) => set(key, Number(e.target.value) as PrintTemplate[typeof key])}
            />
          ))}
        </FormLayout.Grid>

        <FormLayout.Section title="Layout" />
        <div style={{ display: 'flex', gap: '18px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 440px', minWidth: 0 }}>
            <Input
              label="Header (optional)" type="textarea" rows={3} fullWidth
              placeholder="Shown at the top of every page"
              value={template.headerHtml || ''}
              onChange={(e) => set('headerHtml', e.target.value)}
              style={{ fontFamily: 'ui-monospace, Consolas, monospace', fontSize: '12px' }}
            />

            <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '14px' }}>
              <label htmlFor="template-body" style={{
                marginBottom: '6px', color: t.textMuted, fontSize: t.fontSize.sm, fontWeight: 'bold'
              }}>
                Document<span style={{ color: t.error, marginLeft: '4px' }}>*</span>
              </label>
              <textarea
                id="template-body" ref={bodyRef} rows={20} spellCheck={false}
                value={template.htmlContent || ''}
                onChange={(e) => set('htmlContent', e.target.value)}
                style={{
                  fontFamily: 'ui-monospace, Consolas, monospace', fontSize: '12px',
                  lineHeight: 1.5, padding: '10px', borderRadius: t.radius.sm,
                  border: `1px solid ${t.border}`, backgroundColor: t.bgInput,
                  color: t.textMain, resize: 'vertical'
                }}
              />
            </div>

            <Input
              label="Footer (optional)" type="textarea" rows={3} fullWidth
              placeholder="Page number, printed date, signatures…"
              value={template.footerHtml || ''}
              onChange={(e) => set('footerHtml', e.target.value)}
              style={{ fontFamily: 'ui-monospace, Consolas, monospace', fontSize: '12px' }}
            />
          </div>

          {/* Fields in the administrator's words; the token stays internal. */}
          <div style={{
            flex: '1 1 260px', minWidth: '240px', maxHeight: '640px', overflowY: 'auto',
            border: `1px solid ${t.border}`, borderRadius: t.radius.md, padding: '12px'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '2px', color: t.textMain }}>Available Information</div>
            <div style={{ fontSize: t.fontSize.sm, color: t.textMuted, marginBottom: '12px' }}>
              Click to add it where the cursor is.
            </div>
            {groups.map((g) => (
              <div key={g.group} style={{ marginBottom: '14px' }}>
                <div style={{
                  fontSize: t.fontSize.xs, textTransform: 'uppercase', letterSpacing: '0.08em',
                  color: t.textFaint, fontWeight: 'bold', marginBottom: '6px'
                }}>
                  {g.group}
                </div>
                {g.fields.map((f) => (
                  <button
                    key={f.token} type="button" onClick={() => insert(f.token)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                      border: 'none', background: 'none', padding: '4px 0',
                      color: t.textAccent, fontSize: t.fontSize.md
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            ))}
            <div style={{
              fontSize: t.fontSize.sm, color: t.textMuted,
              borderTop: `1px solid ${t.border}`, paddingTop: '10px'
            }}>
              A list repeats between <code>{'{{#rows}}'}</code> and <code>{'{{/rows}}'}</code>.
            </div>
          </div>
        </div>

        {problems.length > 0 && (
          <div style={{
            marginTop: '14px', padding: '12px 14px', borderRadius: t.radius.md,
            border: `1px solid ${t.warning}`, backgroundColor: t.bgSubtle
          }}>
            <strong style={{ color: t.warning }}>Before saving</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: '18px', color: t.textMuted }}>
              {problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </div>
        )}

        <div style={{ marginTop: '14px' }}>
          <Button type="button" variant="secondary" onClick={() => setPreviewing(template)}>
            Preview
          </Button>
        </div>

        {previewing && (
          <PreviewModal
            template={previewing} config={config} t={t} isDarkMode={isDarkMode}
            onClose={() => setPreviewing(null)}
          />
        )}
      </FormLayout>
    );
  }

  // --- Choose a layout for one document -------------------------------------

  if (view.screen === 'choose') {
    const doc = view.doc;
    const saved = forDocument(doc.key);
    const current = currentFor(doc.key);
    const currentDesign = designIdOf(current);
    const shown = previewDesign || currentDesign || DEFAULT_PRINT_TEMPLATE;
    const shownTemplate = renderLayout(shown, doc.key, accent);

    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px', flexWrap: 'wrap' }}>
          <Button variant="secondary" onClick={() => setView({ screen: 'document', doc })}>← Back</Button>
          <h3 style={{ margin: 0, color: t.textMain }}>Choose a layout for {doc.label}</h3>
          {moduleChip(doc.module)}
        </div>
        <p style={{ color: t.textMuted, marginTop: 0, marginBottom: '16px' }}>
          Pick a design and see it below with sample {doc.label.toLowerCase()} details.
          Colour comes from the brand colour on the Church Identity tab.
        </p>

        {/* The ten designs. The card is drawn from the same record that builds
            the printed page, so it cannot show a shape the page does not have. */}
        <div style={{
          display: 'grid', gap: '10px',
          gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))',
          marginBottom: '16px'
        }}>
          {PRINT_TEMPLATE_LIST.map((design) => {
            const inUse = design.id === currentDesign;
            const active = design.id === shown;
            return (
              <button
                key={design.id}
                type="button"
                aria-pressed={active}
                onClick={() => setPreviewDesign(design.id)}
                style={{
                  textAlign: 'left', padding: '11px', cursor: 'pointer',
                  borderRadius: t.radius.lg, font: 'inherit', color: t.textMain,
                  border: `2px solid ${active ? t.primary : t.border}`,
                  backgroundColor: active ? t.primarySoft : t.bgMain
                }}
              >
                <LayoutThumbnail design={design} accent={accent} border={t.border} />
                <div style={{
                  fontWeight: 'bold', fontSize: t.fontSize.md, marginTop: '9px', color: t.textMain,
                  display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap'
                }}>
                  {design.label}
                  {inUse && <Badge tone="success">In use</Badge>}
                </div>
                <div style={{ fontSize: t.fontSize.sm, color: t.textMuted, marginTop: '3px' }}>
                  {design.blurb}
                </div>
              </button>
            );
          })}
        </div>

        {/* What the highlighted design actually does, in plain words. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
          {layoutAttributes(resolvePrintTemplate(shown)).map(([label, value]) => (
            <span
              key={label}
              style={{
                fontSize: t.fontSize.sm, color: t.textMuted, padding: '4px 11px',
                border: `1px solid ${t.border}`, borderRadius: t.radius.pill,
                backgroundColor: t.bgMain
              }}
            >
              {label}: <strong style={{ color: t.textMain }}>{value}</strong>
            </span>
          ))}
        </div>

        <Card style={{ padding: 0, overflow: 'hidden', marginBottom: '16px' }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            gap: '12px', flexWrap: 'wrap', padding: '12px 15px',
            borderBottom: `1px solid ${t.border}`
          }}>
            <span style={{ fontSize: t.fontSize.sm, color: t.textFaint, letterSpacing: '0.08em' }}>
              LIVE PREVIEW — <strong style={{ color: t.textMain }}>{resolvePrintTemplate(shown).label}</strong>
            </span>
            <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
              <Button size="sm" variant="ghost" onClick={() => setPreviewing(shownTemplate)}>
                Full Preview
              </Button>
              <Button
                size="sm" variant="ghost" write
                onClick={() => setView({ screen: 'edit', template: shownTemplate })}
              >
                Edit Template
              </Button>
              <Button
                size="sm" write disabled={saving || shown === currentDesign}
                onClick={() => applyDesign(shown, doc)}
              >
                {shown === currentDesign ? 'In use' : 'Use This Template'}
              </Button>
            </div>
          </div>
          <iframe
            title="Layout preview"
            srcDoc={buildPrintHtml(shownTemplate, sampleFor(doc.key), config, doc.label)}
            style={{ width: '100%', height: '620px', border: 0, backgroundColor: '#FFF', display: 'block' }}
          />
        </Card>

        {/* Layouts this parish has saved or edited, kept separate from the
            designs above so an edited copy is never mistaken for a fresh one. */}
        {saved.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              fontSize: t.fontSize.sm, textTransform: 'uppercase', letterSpacing: '0.08em',
              color: t.textFaint, fontWeight: 'bold', marginBottom: '8px'
            }}>
              Saved for this document
            </div>
            {saved.map((x) => {
              const inUse = current?.id === x.id;
              return (
                <div key={x.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  gap: '10px', flexWrap: 'wrap', padding: '10px 12px', marginBottom: '7px',
                  border: `1px solid ${inUse ? t.primary : t.border}`, borderRadius: t.radius.md,
                  backgroundColor: t.bgMain
                }}>
                  <span style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <strong style={{ color: t.textMain }}>{x.templateName}</strong>
                    {statusBadges(x)}
                  </span>
                  <span style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {previewButton(x)}
                    {!inUse && x.isActive && (
                      <Button size="sm" write onClick={() => setConfirming({ template: x, doc })}>
                        Use This Layout
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" write onClick={() => setView({ screen: 'edit', template: { ...x } })}>
                      Edit
                    </Button>
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <Button variant="secondary" write onClick={() => startCreate(doc.key)}>
          + Write my own layout
        </Button>

        {previewing && (
          <PreviewModal
            template={previewing} config={config} t={t} isDarkMode={isDarkMode}
            onClose={() => setPreviewing(null)}
          />
        )}
        <ConfirmDialog
          open={Boolean(confirming)}
          title="Use this layout?"
          message={confirming
            ? `Use "${confirming.template.templateName}" every time a ${confirming.doc.label} is printed?`
            : ''}
          confirmLabel="Use This Layout"
          onConfirm={() => confirming && applyTemplate(confirming.template, confirming.doc)}
          onCancel={() => setConfirming(null)}
        />
      </>
    );
  }

  // --- One document ---------------------------------------------------------

  if (view.screen === 'document') {
    const doc = view.doc;
    const current = currentFor(doc.key);
    const all = forDocument(doc.key);

    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px', flexWrap: 'wrap' }}>
          <Button variant="secondary" onClick={() => setView({ screen: 'list' })}>← All documents</Button>
          <h3 style={{ margin: 0, color: t.textMain }}>{doc.label}</h3>
          {moduleChip(doc.module)}
        </div>
        <p style={{ color: t.textMuted, marginTop: 0 }}>{doc.describes}</p>

        {/* Which rule this document follows at print time, said plainly, so the
            behaviour is not a surprise to whoever sets it up. */}
        <p style={{ color: t.textMuted, marginTop: 0, fontSize: t.fontSize.sm }}>
          {doc.selectable
            ? 'When this is printed, staff are offered every layout below and can pick one. The layout in use is what they get if they do not choose.'
            : 'This always prints with the layout in use — staff are not asked to choose.'}
        </p>

        <Card style={{ padding: '20px', marginBottom: '18px' }}>
          <div style={{ fontSize: t.fontSize.sm, color: t.textFaint, marginBottom: '6px' }}>
            CURRENT LAYOUT
          </div>
          {current ? (
            <>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: '18px', color: t.textMain }}>{current.templateName}</strong>
                <Badge tone="success">In use</Badge>
              </div>
              <div style={{ fontSize: t.fontSize.sm, color: t.textFaint, margin: '4px 0 14px' }}>
                {current.pageSize || 'A4'} {current.orientation === 'LANDSCAPE' ? 'Landscape' : 'Portrait'}
                {missingNote(current)}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {previewButton(current, 'md')}
                <Button variant="secondary" write onClick={() => setView({ screen: 'choose', doc })}>
                  Change Layout
                </Button>
                <Button variant="ghost" write onClick={() => setView({ screen: 'edit', template: { ...current } })}>
                  Edit Layout
                </Button>
              </div>
            </>
          ) : (
            <>
              <p style={{ margin: '0 0 14px', color: t.textMuted }}>
                {doc.builtIn
                  ? 'No layout of your own is set, so this prints with the standard built-in layout. Choose one to change how it looks.'
                  : 'No layout is set, so printing this document reports that one is needed.'}
              </p>
              <Button write onClick={() => setView({ screen: 'choose', doc })}>
                Choose a layout
              </Button>
            </>
          )}
        </Card>

        {all.length > 1 && (
          <Card style={{ padding: '16px', marginBottom: '18px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '10px', color: t.textMain }}>Other layouts for this document</div>
            {all.filter((x) => x.id !== current?.id).map((x) => (
              <div key={x.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: '10px', flexWrap: 'wrap', padding: '8px 0',
                borderTop: `1px solid ${t.border}`
              }}>
                <span style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', color: t.textMain }}>
                  {x.templateName} {statusBadges(x)}
                </span>
                <span style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {previewButton(x)}
                  <Button size="sm" variant="ghost" write onClick={() => toggleActive(x)}>
                    {x.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button size="sm" variant="danger" write onClick={() => setDeleting(x)}>Delete</Button>
                </span>
              </div>
            ))}
          </Card>
        )}

        <div style={{ fontSize: t.fontSize.sm, color: t.textFaint }}>
          Printed from: {doc.producedAt}
          <br />
          <span>Module <strong style={{ color: t.textMuted }}>{doc.module}</strong> · type {doc.key}</span>
        </div>

        {previewing && (
          <PreviewModal
            template={previewing} config={config} t={t} isDarkMode={isDarkMode}
            onClose={() => setPreviewing(null)}
          />
        )}
        <ConfirmDialog
          open={Boolean(deleting)}
          title={`Delete "${deleting?.templateName || 'layout'}"?`}
          message="This layout will no longer be available for this document."
          confirmLabel="Delete"
          onConfirm={confirmDelete}
          onCancel={() => setDeleting(null)}
        />
      </>
    );
  }

  // --- The document list ----------------------------------------------------

  return (
    <>
      <Card style={{ marginBottom: '18px', padding: '15px', color: t.textMuted, fontSize: t.fontSize.md }}>
        Choose how each document will look when it is printed or saved as PDF.
      </Card>

      <div style={{
        display: 'flex', gap: '10px', alignItems: 'flex-end',
        flexWrap: 'wrap', marginBottom: '16px'
      }}>
        <SearchBar
          value={search}
          onChange={setSearch}
          onClear={() => setSearch('')}
          placeholder="Search documents…"
          fieldStyle={{ flex: '1 1 260px', minWidth: '200px' }}
          style={{ flex: '1 1 260px', marginBottom: 0 }}
        />
        <Select
          label="Category"
          options={['All', ...DOCUMENT_CATEGORIES].map((c) => ({ value: c, label: c }))}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          containerStyle={{ marginBottom: 0, minWidth: '170px' }}
          isDarkMode={isDarkMode}
        />
        <Button
          variant="secondary" write
          onClick={() => setBulk({ design: DEFAULT_PRINT_TEMPLATE, running: false, done: 0 })}
        >
          Use one design everywhere
        </Button>
        <Button write onClick={() => startCreate()}>+ Create Template</Button>
      </div>

      {loading && <p style={{ color: t.textMuted }}>Loading…</p>}

      {!loading && visibleDocuments.length === 0 && (
        <p style={{ color: t.textMuted }}>No documents match that search.</p>
      )}

      {DOCUMENT_CATEGORIES.map((cat) => {
        const docs = visibleDocuments.filter((d) => d.category === cat);
        if (docs.length === 0) return null;
        return (
          <div key={cat} style={{ marginBottom: '26px' }}>
            <div style={{
              fontSize: t.fontSize.sm, textTransform: 'uppercase', letterSpacing: '0.08em',
              color: t.textFaint, fontWeight: 'bold', marginBottom: '8px'
            }}>
              {cat}
            </div>

            {docs.map((d) => {
              const current = currentFor(d.key);
              return (
                <div
                  key={d.key}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    gap: '12px', flexWrap: 'wrap',
                    padding: '14px 16px', marginBottom: '8px',
                    border: `1px solid ${t.border}`, borderRadius: t.radius.md,
                    backgroundColor: t.bgMain
                  }}
                >
                  <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 'bold', color: t.textMain }}>{d.label}</span>
                      {moduleChip(d.module)}
                    </div>
                    <div style={{ fontSize: t.fontSize.sm, color: t.textFaint }}>{d.describes}</div>
                  </div>

                  <div style={{ flex: '0 1 220px', minWidth: '150px' }}>
                    {current ? (
                      <>
                        <div style={{ color: t.textMain }}>{current.templateName}</div>
                        <div style={{ fontSize: t.fontSize.sm, color: t.textFaint }}>In use</div>
                        {missingNote(current)}
                      </>
                    ) : d.builtIn ? (
                      // Prints with the standard design until one is chosen, so
                      // it is not "not set" in any way that stops a print.
                      <>
                        <Badge tone="success">Built-in layout</Badge>
                        <div style={{ fontSize: t.fontSize.sm, color: t.textFaint }}>Prints now · customise any time</div>
                      </>
                    ) : (
                      <Badge tone="warning">Not set</Badge>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {current
                      ? previewButton(current)
                      : d.builtIn && previewButton(renderLayout(DEFAULT_PRINT_TEMPLATE, d.key, accent))}
                    <Button size="sm" variant="secondary" onClick={() => setView({ screen: 'document', doc: d })}>
                      {current ? 'Change' : 'Set up'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {previewing && (
        <PreviewModal
          template={previewing} config={config} t={t} isDarkMode={isDarkMode}
          onClose={() => setPreviewing(null)}
        />
      )}

      {bulk && (
        <Modal
          title="Use one design everywhere"
          width="720px"
          onClose={() => !bulk.running && setBulk(null)}
          isDarkMode={isDarkMode}
          footer={
            <>
              <Button variant="secondary" disabled={bulk.running} onClick={() => setBulk(null)}>
                Cancel
              </Button>
              <Button write disabled={bulk.running} onClick={() => applyDesignToAll(bulk.design)}>
                {bulk.running
                  ? `Applying… ${bulk.done} of ${PRINT_DOCUMENTS.length}`
                  : `Apply to all ${PRINT_DOCUMENTS.length} documents`}
              </Button>
            </>
          }
        >
          <p style={{ color: t.textMuted, marginTop: 0 }}>
            Every document — certificates, receipts, records and registers — will print with
            the design you pick here, in your parish's own colour. Each document still keeps
            its own layout afterwards, so you can change any single one later.
          </p>

          <div style={{
            display: 'grid', gap: '10px',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))'
          }}>
            {PRINT_TEMPLATE_LIST.map((design) => {
              const active = design.id === bulk.design;
              return (
                <button
                  key={design.id}
                  type="button"
                  aria-pressed={active}
                  disabled={bulk.running}
                  onClick={() => setBulk({ ...bulk, design: design.id })}
                  style={{
                    textAlign: 'left', padding: '10px', cursor: bulk.running ? 'default' : 'pointer',
                    borderRadius: t.radius.lg, font: 'inherit', color: t.textMain,
                    border: `2px solid ${active ? t.primary : t.border}`,
                    backgroundColor: active ? t.primarySoft : t.bgMain
                  }}
                >
                  <LayoutThumbnail design={design} accent={accent} height="62px" border={t.border} />
                  <div style={{ fontWeight: 'bold', fontSize: t.fontSize.sm, marginTop: '7px', color: t.textMain }}>
                    {design.label}
                  </div>
                </button>
              );
            })}
          </div>

          <p style={{ color: t.textFaint, fontSize: t.fontSize.sm, marginBottom: 0 }}>
            Layouts you have written yourself are left alone — this only changes which design
            each document prints with.
          </p>
        </Modal>
      )}

      {creating && (
        <Modal
          title="Create Template"
          width="520px"
          onClose={() => setCreating(null)}
          isDarkMode={isDarkMode}
          footer={
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setCreating(null)}>Cancel</Button>
              <Button write onClick={doCreate}>Create Template</Button>
            </div>
          }
        >
          <Input
            label="Template name" required fullWidth
            placeholder="e.g. Classic Certificate"
            value={creating.name}
            onChange={(e) => setCreating({ ...creating, name: e.target.value })}
          />
          <Select
            label="For which document" required fullWidth
            placeholder="Choose a document…"
            options={documentOptions}
            value={creating.documentType}
            onChange={(e) => setCreating({
              ...creating,
              documentType: e.target.value,
              basedOn: firstLayoutValue(e.target.value)
            })}
            isDarkMode={isDarkMode}
          />
          {/* The layouts on offer depend on the document, so this only appears
              once one has been chosen. */}
          {creating.documentType && (
            <Select
              label="Start from" fullWidth
              options={[
                ...PRINT_TEMPLATE_LIST.map((d) => ({
                  value: `layout:${d.id}`, label: `${d.label} — ${d.blurb}`
                })),
                ...forDocument(creating.documentType).map((x) => ({
                  value: String(x.id), label: `A copy of "${x.templateName}"`
                }))
              ]}
              value={creating.basedOn}
              onChange={(e) => setCreating({ ...creating, basedOn: e.target.value })}
              isDarkMode={isDarkMode}
            />
          )}
          <p style={{ color: t.textMuted, fontSize: t.fontSize.sm, margin: '10px 0 0' }}>
            The editor opens next, already filled in — you can print it as it is or change it.
          </p>
        </Modal>
      )}
    </>
  );
};

/**
 * The layout as it will print, drawn in an isolated frame.
 *
 * The frame carries the document's own stylesheet, so nothing of the
 * application's styling reaches the paper and nothing of the template's reaches
 * the application. It is the same HTML the printer is given — not a picture of
 * the screen.
 */
const PreviewModal = ({ template, config, onClose, isDarkMode, t }: {
  template: PrintTemplate;
  config: ReturnType<typeof useChurchConfig>;
  onClose: () => void;
  isDarkMode?: boolean;
  t: SettingsSectionProps['t'];
}) => {
  const html = useMemo(
    () => buildPrintHtml(template, sampleFor(template.documentType), config, template.templateName),
    [template, config]
  );

  return (
    <Modal
      title={`${template.templateName || 'Layout'} — ${documentLabel(template.documentType)}`}
      width="920px"
      onClose={onClose}
      isDarkMode={isDarkMode}
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
    >
      <div style={{
        marginBottom: '10px', padding: '8px 12px', borderRadius: t.radius.sm,
        backgroundColor: t.bgSubtle, color: t.textMuted, fontSize: t.fontSize.sm
      }}>
        Example values, not real records — they show where each piece of information lands.
      </div>
      <iframe
        title="Layout preview"
        srcDoc={html}
        style={{ width: '100%', height: '62vh', border: `1px solid ${t.border}`, background: '#FFF' }}
      />
    </Modal>
  );
};

export default TemplatesSection;
